import {
  GAME_TYPE,
  type Game,
  type GameType,
  type LanguageCode,
  isPartialGameErrorText,
  isPartialGameMultipleChoiceQuestion,
  isPartialGameNoInteraction,
  isPartialGameOpenQuestion,
  isPartialGameOrderingCards,
  isPartialGamePictureSpot,
  isPartialGameSwipingCards,
} from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DeepPartial } from 'ts-essentials';

import MGameplayPreview from 'Components/modal/MGameplayPreview/MGameplayPreview';
import MModal from 'Components/modal/MModal/MModal';
import MModalOverlay from 'Components/modal/MModalOverlay/MModalOverlay';
import { MTranslateModal } from 'Components/modal/MTranslateModal/MTranslateModal';
import SCallout from 'Components/structural/SCallout/SCallout';
import SContentEditionBar from 'Components/structural/SContentEditionBar/SContentEditionBar';
import STable from 'Components/structural/STable/STable';
import { STranslationInterface } from 'Components/structural/STranslationInterface/STranslationInterface';
import { isApiError } from 'Libs/http/utils';
import { useAppDispatch } from 'Libs/redux/store';
import { useTypedSelector } from 'Libs/redux/utils';
import { redirect } from 'Libs/routing/redirect';
import { enqueueBasicAlert } from 'Pages/AlertManager';
import { getLanguageFromCode, getLanguagesAvailableWithCode, type Language } from 'Services/language/languageService';
import { inferInitialSelectedLanguage } from 'Services/language/routing';
import { ROLES, getCurrentUserRoles } from 'Services/role/roleService';
import { getLanguageSegmentationGroupItemsFromApp } from 'Services/segmentation/segmentationService';
import { contentMetadataSelector, selectLastContribution } from 'Store/entities/content/content/content.selectors';
import { fetchContentById } from 'Store/entities/content/content/content.thunks';
import { ContentEntity } from 'Store/entities/content/content.types';
import { fetchContentPreviewUrl } from 'Store/entities/content/content/content.hook';

import { ErrorTextForm } from '../GameplaysForm/ErrorTextForm';
import { MCQForm } from '../GameplaysForm/MCQForm';
import { NoInteractionForm } from '../GameplaysForm/NoInteractionForm';
import { OpenQuestionForm } from '../GameplaysForm/OpenQuestionForm';
import { OrderingCardsForm } from '../GameplaysForm/OrderingCardsForm';
import { PictureSpotForm } from '../GameplaysForm/PictureSpotForm';
import { SwipingCardsForm } from '../GameplaysForm/SwipingCardsForm';
import { ALERT_DURATION } from '../GameplaysForm/gameplay.constants';
import styles from './ContentEdition.style';
import { GameplayChoice } from './GameplayChoice';
import { Panel } from './Panel';
import {
  formatContentForTranslationInterface,
  formatTranslationInterfaceForContent,
  getContentId,
  getFirstValidatedLanguage,
} from './contentEdition.utils';
import { ContentFormValues, getContributorsColumns, useContentActions, useSubmitContentForm } from './contentForm.hook';

type InferFunctionArguments<T extends (...args: any[]) => any> = T extends (...args: infer U) => any ? U : never;

export const ContentForm = ({ content }: { content: ContentEntity }) => {
  const userRoles = getCurrentUserRoles();
  const dispatch = useAppDispatch();
  const contentId = getContentId();

  const defaultLanguage = inferInitialSelectedLanguage(Object.keys(content.statuses) as LanguageCode[], true);

  const [contentLanguageCode, setLanguageCode] = useState<LanguageCode>(defaultLanguage);
  const currentLanguage = getLanguageFromCode(contentLanguageCode);
  const firstValidatedLanguage = getFirstValidatedLanguage(content.statuses);

  const { error } = useTypedSelector(contentMetadataSelector);

  const [visiblePreviewModal, displayPreviewModal] = useState(false);
  const [visibleGameplayModal, displayGameplayModal] = useState(false);
  const [visibleTranslationModal, displayTranslationModal] = useState(false);
  const [languageToDelete, setLanguageToDelete] = useState<Language | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [visibleWarningModal, displayWarningModal] = useState(false);
  const [warningType, setWarningType] = useState<keyof typeof warnings>('none');
  const [originalLanguage, setOriginalLanguage] = useState<Language>(firstValidatedLanguage || currentLanguage);

  const warningModalActionRef = useRef<() => void>(() => {});

  const languageSegmentations = useMemo(() => getLanguageSegmentationGroupItemsFromApp(), []);
  const languagesWithCode = useMemo(() => getLanguagesAvailableWithCode(), []);
  const alreadySelectedLanguagesWithCode = useMemo(
    () => languagesWithCode.filter((lang) => Object.keys(content.statuses).includes(lang.languageCode)),
    [languagesWithCode, content.statuses],
  );

  useEffect(() => {
    if (error !== null && typeof error !== 'string') {
      setError('root', error);
    }
  }, [error]);

  const defaultValues = useMemo(
    () => ({
      name: content.name || { [contentLanguageCode]: '' },
      identifier: content.identifier || '',
      thematic: content.thematic.id || 0,
      image: content.coverImage || null,
      knowMore: content.knowMore || { [contentLanguageCode]: '' },
      game: content.game,
    }),
    [content, contentLanguageCode],
  );

  const { setValue, watch, setError, clearErrors, reset, getValues, ...restOfFormMethods } = useForm<ContentFormValues>(
    {
      defaultValues,
    },
  );

  const {
    handleSaveContent,
    handleValidation,
    handleUnValidation,
    onAskTranslation,
    handleSaveTranslation,
    onContentAskValidation,
    onUpdateValidation,
    contributorData,
    onAutomaticTranslation,
    onDeleteLanguage,
  } = useSubmitContentForm({
    content,
    watch,
    reset,
    getValues,
    isMasterAdmin: userRoles.some(
      (role) => role.name === ROLES.ADMIN || role.name === ROLES.ROOT || role.name === ROLES.MASTER_ADMIN,
    ),
    contentLanguageCode,
    displayTranslationModal,
    languagesWithCode,
  });

  const { handleDuplicateContent, handleArchiveContent, handleUnarchiveContent } = useContentActions();

  const handleWarningBeforeUnload = useCallback(
    (validateCallback: () => void = () => {}) => {
      if (!restOfFormMethods.formState.isDirty) {
        return validateCallback();
      }

      warningModalActionRef.current = () => {
        displayWarningModal(false);
        validateCallback();
      };

      setWarningType('unload');
      displayWarningModal(true);
    },
    [restOfFormMethods.formState.isDirty],
  );

  const handleWarningBeforeArchive = useCallback(() => {
    setWarningType('archive');
    displayWarningModal(true);
  }, []);

  const handleWarningBeforeUnarchive = useCallback(() => {
    setWarningType('unarchive');
    displayWarningModal(true);
  }, []);

  const options = useMemo(
    () => [
      {
        id: 1,
        text: t('content_translation:content_edition_bar.duplicate_content'),
        onClick: () => handleWarningBeforeUnload(() => handleDuplicateContent(content.id)),
      },
      {
        id: 2,
        text: content.archived
          ? t('content_translation:content_edition_bar.unarchive_content')
          : t('content_translation:content_edition_bar.archive_content'),
        onClick: content.archived
          ? () => handleWarningBeforeUnload(handleWarningBeforeUnarchive)
          : () => handleWarningBeforeUnload(handleWarningBeforeArchive),
      },
      {
        id: 3,
        text: t('content_translation:content_edition_bar.see_contributors'),
        onClick: () => displayContributorsModal(true),
      },
    ],
    [handleWarningBeforeUnload, handleDuplicateContent, handleArchiveContent, content, handleUnarchiveContent],
  );

  const warnings = useMemo(
    () => ({
      unload: {
        title: t('structural_component:s_translation_interface.warning_before_unload_title'),
        description: t('structural_component:s_translation_interface.warning_before_unload_description'),
        labelActionButton: t('structural_component:s_translation_interface.warning_before_unload_action_button'),
        labelSecondButton: t('structural_component:s_translation_interface.warning_before_unload_second_button'),
        onAction: () => warningModalActionRef.current(),
      },
      archive: {
        title: t('contents:modals.archive.title'),
        description: t('contents:modals.archive.description'),
        labelActionButton: t('contents:modals.archive.confirm'),
        labelSecondButton: t('contents:modals.archive.cancel'),
        onAction: () => {
          handleArchiveContent(content.id);
          displayWarningModal(false);
        },
      },
      unarchive: {
        title: t('contents:modals.unarchive.title'),
        description: t('contents:modals.unarchive.description'),
        labelActionButton: t('contents:modals.unarchive.confirm'),
        labelSecondButton: t('contents:modals.unarchive.cancel'),
        onAction: () => {
          handleUnarchiveContent(content.id);
          displayWarningModal(false);
        },
      },
      none: {},
    }),
    [content.id, handleArchiveContent, handleUnarchiveContent, displayWarningModal],
  );

  useEffect(() => {
    if (contentId) {
      dispatch(fetchContentById({ contentId }));

      const fetchUrl = async () => {
        const result = await fetchContentPreviewUrl(contentId, contentLanguageCode);
        if (!isApiError(result)) {
          setPreviewUrl(result.contentPreviewUrl);
        } else {
          enqueueBasicAlert(
            {
              id: 'content_preview_failed',
              status: 'error',
              title: t('contents:edition.update_fail'),
            },
            ALERT_DURATION,
          );
        }
      };

      fetchUrl();
    }
  }, [contentId, contentLanguageCode, dispatch]);

  const resetGameplay = () => {
    setValue('game', null, { shouldDirty: true });
  };

  const translatedFields = useMemo(() => formatContentForTranslationInterface(watch()), [watch()]);

  const contentGameValues = {
    language: contentLanguageCode,
    isGameplayAlreadyChosen: !!content.game?.type,
    editGameplay: () => {
      displayGameplayModal(true);
    },
    disabled: content.archived,
  };

  const [contributorsModal, displayContributorsModal] = useState(false);

  return (
    <FormProvider
      {...restOfFormMethods}
      watch={watch}
      setValue={(...args: InferFunctionArguments<typeof setValue>) => {
        const [name, value, config] = args;
        setValue(name, value, {
          ...config,
          shouldDirty: true,
        });
      }}
      setError={setError}
      clearErrors={clearErrors}
      reset={reset}
      getValues={getValues}
    >
      <SContentEditionBar
        title={watch(`name.${contentLanguageCode}`) || ''}
        identifier={watch('identifier')}
        lastSavedDate={selectLastContribution(content)}
        style={styles.editionBar}
        onBack={() => handleWarningBeforeUnload(() => redirect('/content'))}
        onSave={handleSaveContent}
        onAskValidation={onContentAskValidation}
        onUpdateValidation={onUpdateValidation}
        onUnValidate={handleUnValidation}
        onValidate={handleValidation}
        onDeleteLanguage={(languageCode) => {
          setLanguageToDelete(getLanguageFromCode(languageCode));
        }}
        onRequestTranslation={() => handleWarningBeforeUnload(() => displayTranslationModal(true))}
        onPreviewClick={() => displayPreviewModal(true)}
        onSaveTranslation={handleSaveTranslation}
        selectedLanguageCode={contentLanguageCode}
        contentStatuses={content.statuses}
        isFormDirty={restOfFormMethods.formState.isDirty}
        options={options}
        setLanguageCode={(language: LanguageCode) => handleWarningBeforeUnload(() => setLanguageCode(language))}
        archived={content.archived}
      />

      {content.archived && (
        <SCallout
          type="standard-dark"
          title={t('contents:callouts.archived.title')}
          description={t('contents:callouts.archived.description')}
          banner={{
            illustration: 'knowledge',
            illustrationColor: 'GREY_DARK',
          }}
          action={{
            label: t('contents:callouts.archived.button_label'),
            onAction: () => handleWarningBeforeUnarchive(),
            buttonType: 'button',
          }}
          style={styles.disabledCallout}
        />
      )}

      <div style={styles.container}>
        <MModalOverlay visible={content.archived} style={styles.disabledOverlay} />

        {content.statuses[contentLanguageCode] !== 'waiting-translation' && <Panel language={contentLanguageCode} />}

        <MModal
          type="dialog"
          visible={visibleWarningModal}
          actionButtonType="destructive"
          onCloseModal={() => displayWarningModal(false)}
          onSecondAction={() => displayWarningModal(false)}
          showExitCross
          {...warnings[warningType]}
        />

        <MModal
          onCloseModal={() => setLanguageToDelete(null)}
          visible={!!languageToDelete}
          type="dialog"
          title={t('gameplays:delete_translation_modal.title', { language: languageToDelete?.languageName })}
          description={t('gameplays:delete_translation_modal.description', {
            language: languageToDelete?.languageName,
          })}
          labelActionButton={t('gameplays:delete_translation_modal.confirm')}
          labelSecondButton={t('gameplays:delete_translation_modal.cancel')}
          onAction={() => {
            setLanguageToDelete(null);
            if (languageToDelete) {
              onDeleteLanguage(languageToDelete.languageCode);
            }
          }}
          onSecondAction={() => setLanguageToDelete(null)}
          showExitCross
          actionButtonType="destructive"
        />
        <MModal
          onCloseModal={() => displayGameplayModal(false)}
          visible={visibleGameplayModal}
          type="dialog"
          title={t('gameplays:change_gameplay.title')}
          description={t('gameplays:change_gameplay.description')}
          labelActionButton={t('gameplays:change_gameplay.confirm')}
          labelSecondButton={t('gameplays:change_gameplay.cancel')}
          onAction={() => {
            displayGameplayModal(false);
            resetGameplay();
          }}
          onSecondAction={() => displayGameplayModal(false)}
          showExitCross
          actionButtonType="destructive"
        />
        <MModal
          onCloseModal={() => displayContributorsModal(false)}
          visible={contributorsModal}
          type="dialog"
          title={'Contributions'}
          showExitCross
          style={styles.contributorsModal}
        >
          <STable title="" total={contributorData.length} columns={getContributorsColumns()} data={contributorData} />
        </MModal>
        <MTranslateModal
          visible={visibleTranslationModal}
          onCancel={() => displayTranslationModal(false)}
          onAction={onAskTranslation}
          isActionLoading={false}
          languageSeg={languageSegmentations}
          alreadySelectedLanguages={alreadySelectedLanguagesWithCode}
          title={t('contents:translation.title')}
          labelActionButton={t('contents:translation.label_action')}
          labelSecondButton={t('contents:translation.label_cancel')}
          explanationTitle={t('contents:translation.explanation_title')}
          explanation={t('contents:translation.explanation')}
          filterLabel={t('contents:translation.filter_label')}
        />
        {previewUrl && visiblePreviewModal && content.statuses[contentLanguageCode] === 'validated' && (
          <MGameplayPreview
            visible={visiblePreviewModal}
            onCloseModal={() => {
              displayPreviewModal(false);
            }}
            title={watch(`name.${contentLanguageCode}`) || ''}
            subtitle={watch('identifier')}
            url={previewUrl}
            qrCodeLabel={t('gameplays:scan_preview')}
            refreshLabel={t('gameplays:refresh_game')}
            gameplay={content.game?.type || GAME_TYPE.MCQ}
          />
        )}
        {!watch('game.type') && (
          <GameplayChoice
            gameplayType={watch('game.type') || GAME_TYPE.MCQ}
            selectGameplayType={(gameplay: GameType) => {
              setValue('game.type', gameplay, { shouldDirty: true });
            }}
          />
        )}
        {content.statuses[contentLanguageCode] === 'waiting-translation' ? (
          <STranslationInterface
            translatedLanguage={currentLanguage}
            originalLanguage={originalLanguage}
            availableLanguages={languagesWithCode.filter(
              (lang) =>
                content.statuses[lang.languageCode] === 'validated' && lang.languageCode !== contentLanguageCode,
            )}
            changeOriginalLanguage={setOriginalLanguage}
            fields={translatedFields}
            onFieldsChange={(fields) => {
              const { name, game } = formatTranslationInterfaceForContent(fields, content.game?.type as GameType);
              if (name) {
                setValue('name', name, { shouldDirty: true });
              }
              setValue('game', { ...watch('game'), ...game } as Game, { shouldDirty: true });
            }}
            isFormDirty={restOfFormMethods.formState.isDirty}
            onAutomaticTranslation={onAutomaticTranslation}
          />
        ) : (
          <>
            {watch('game.type') && isPartialGameMultipleChoiceQuestion(watch('game') as DeepPartial<Game>) && (
              <MCQForm {...contentGameValues} />
            )}
            {watch('game.type') && isPartialGameOpenQuestion(watch('game') as DeepPartial<Game>) && (
              <OpenQuestionForm {...contentGameValues} />
            )}
            {watch('game.type') && isPartialGameOrderingCards(watch('game') as DeepPartial<Game>) && (
              <OrderingCardsForm {...contentGameValues} />
            )}
            {watch('game.type') && isPartialGameSwipingCards(watch('game') as DeepPartial<Game>) && (
              <SwipingCardsForm {...contentGameValues} />
            )}
            {watch('game.type') && isPartialGameErrorText(watch('game') as DeepPartial<Game>) && (
              <ErrorTextForm {...contentGameValues} />
            )}
            {watch('game.type') && isPartialGameNoInteraction(watch('game') as DeepPartial<Game>) && (
              <NoInteractionForm {...contentGameValues} />
            )}
            {watch('game.type') && isPartialGamePictureSpot(watch('game') as DeepPartial<Game>) && (
              <PictureSpotForm {...contentGameValues} />
            )}
          </>
        )}
      </div>
    </FormProvider>
  );
};
